/**
 * Utility function for copying text to clipboard with fallback support
 * Works with HTTP, HTTPS, and all network scenarios
 */

export async function copyToClipboard(text) {
  // Try modern Clipboard API first (works on HTTPS and localhost)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return { success: true, message: "Copied to clipboard!" };
    } catch (err) {
      console.error("Clipboard API failed:", err);
      return fallbackCopyToClipboard(text);
    }
  }

  return fallbackCopyToClipboard(text);
}

function fallbackCopyToClipboard(text) {
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);

    textArea.select();
    textArea.setSelectionRange(0, 99999); // For mobile devices

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);

    if (successful) {
      return { success: true, message: "Copied to clipboard!" };
    }

    return { success: false, message: "Failed to copy. Please try again." };
  } catch (err) {
    console.error('Fallback copy failed:', err);
    return { success: false, message: 'Clipboard access not available.' };
  }
}

/**
 * Show toast notification
 * @param {string} message - Message to show
 * @param {string} type - 'success', 'error', 'info', 'warning'
 */
export function showToast(message, type = 'info') {
  const toastContainer = getOrCreateToastContainer();

  // Silently fail if DOM is not ready
  if (!toastContainer) {
    console.warn('Toast container could not be created:', message);
    return;
  }

  const toast = createToastElement(message, type);
  toastContainer.appendChild(toast);

  // Auto remove after 3 seconds
  setTimeout(() => {
    toast.classList.add('fade-out');
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

function getOrCreateToastContainer() {
  if (!document || !document.body) {
    return null;
  }

  let container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 10px;
      max-width: 400px;
    `;
    document.body.appendChild(container);
  }
  return container;
}

function createToastElement(message, type) {
  const toast = document.createElement('div');
  const colors = {
    success: { bg: '#4CAF50', border: '#45a049' },
    error: { bg: '#f44336', border: '#da190b' },
    info: { bg: '#2196F3', border: '#0b7dda' },
    warning: { bg: '#ff9800', border: '#e68900' }
  };

  const color = colors[type] || colors.info;

  toast.style.cssText = `
    background-color: ${color.bg};
    color: white;
    padding: 18px 22px;
    min-height: 56px;
    box-sizing: border-box;
    border-radius: 12px;
    border-left: 4px solid ${color.border};
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    animation: slideIn 0.3s ease-in-out;
    font-size: 14px;
    font-weight: 500;
    transition: opacity 0.3s ease;
  `;

  toast.textContent = message;

  // Add fade out class styles
  toast.classList.add('toast');

  return toast;
}

// Add CSS for animations (defer until DOM is ready)
function injectStyles() {
  if (!document || !document.head) {
    setTimeout(injectStyles, 100);
    return;
  }

  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideIn {
      from {
        transform: translateX(400px);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    .fade-out {
      animation: slideOut 0.3s ease-in-out;
    }

    @keyframes slideOut {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(400px);
        opacity: 0;
      }
    }
  `;

  if (!document.head.querySelector('style[data-toast-animations]')) {
    style.setAttribute('data-toast-animations', 'true');
    document.head.appendChild(style);
  }
}

// Inject styles when DOM is ready
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectStyles);
  } else {
    injectStyles();
  }
}

export default copyToClipboard;
